import React, { useEffect, useState, createContext, useContext, useMemo } from "react";
import { ManualAuthProvider } from "./ManualAuthProvider";
import { AuthProvider as OidcAuthProvider } from "./AuthProvider";
import { isMyGovSkipEnabled } from "./authConfig";
import { logger } from "../../shared/utils/logger.client";
import { useTranslation } from "react-i18next";

type AuthMethod = "MANUAL" | "MYGOV" | "EIDAS";

interface AuthRouterContextType {
  setAuthMethod: (method: AuthMethod) => void;
  currentMethod: AuthMethod;
}

const AuthRouterContext = createContext<AuthRouterContextType | undefined>(undefined);

export const useAuthRouter = (): AuthRouterContextType => {
  const ctx = useContext(AuthRouterContext);
  if (!ctx) throw new Error("useAuthRouter must be used within AuthRouterProvider");
  return ctx;
};

const CONFIG_TIMEOUT_MS = 5000;
const STORAGE_KEY = "loggedMethod";
const SKIP_KEY = "skipAuth";

export const AuthRouterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authReady, setAuthReady] = useState(false);
  const [status, setStatus] = useState("boot");
  const [currentMethod, setCurrentMethod] = useState<AuthMethod>("MYGOV");
  const { t } = useTranslation();

  const safeGet = (key: string): string | null => {
    try { return localStorage.getItem(key); } catch { return null; }
  };
  const safeSet = (key: string, val: string) => {
    try { localStorage.setItem(key, val); } catch { /* ignore */ }
  };
  const withTimeout = <T,>(p: Promise<T>, ms: number) =>
      Promise.race<T>([
        p,
        new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`config timeout after ${ms}ms`)), ms)),
      ]);

  useEffect(() => {
    let mounted = true;
    logger.debug("[AuthRouter] init");

    const init = async () => {
      setStatus("checking_local_storage");
      try {
        const storedMethod = safeGet(STORAGE_KEY) as AuthMethod | null;
        const skipFlag = safeGet(SKIP_KEY) === "1";

        logger.debug("Auth values", { storedMethod, skipFlag });

        if (skipFlag) {
          if (!mounted) return;
          setCurrentMethod("MANUAL");
          safeSet(STORAGE_KEY, "MANUAL");
          logger.info("[AuthRouter] skipAuth=1 → forcing MANUAL");
        } else if (storedMethod === "MANUAL" || storedMethod === "MYGOV" || storedMethod === "EIDAS") {
          if (!mounted) return;
          setCurrentMethod(storedMethod);
          logger.info("[AuthRouter] using stored method:", storedMethod);
        } else {
          logger.debug("[AuthRouter] isMyGovSkipEnabled");
          setStatus("fetching_config");
          const skip = await withTimeout(isMyGovSkipEnabled(), CONFIG_TIMEOUT_MS);
          logger.debug("[AuthRouter] isMyGovSkipEnabled ->", skip);
          if (!mounted) return;
          const resolved: AuthMethod = skip ? "MANUAL" : "MYGOV";
          setCurrentMethod(resolved);
          safeSet(STORAGE_KEY, resolved);
          logger.debug("[AuthRouter] resolved method:", resolved);
        }

        setStatus("ready");
      } catch (err) {
        logger.error("[AuthRouter] config check FAILED:", err);
        if (!mounted) return;
        setCurrentMethod("MYGOV");
        setStatus("fallback_ready");
      } finally {
        if (!mounted) return;
        setAuthReady(true);
        logger.debug("[AuthRouter] ready");
      }
    };

    void init();

    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key === STORAGE_KEY || e.key === SKIP_KEY) {
        const method = (safeGet(STORAGE_KEY) as AuthMethod | null) ?? "MYGOV";
        const skip = safeGet(SKIP_KEY) === "1";
        const next: AuthMethod = skip ? "MANUAL" : method;
        logger.info("[AuthRouter] storage event → switching method to:", next);
        setCurrentMethod(next);
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      mounted = false;
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setAuthMethod = (method: AuthMethod) => {
    logger.debug(`[AuthRouter] Switching auth method to: ${method}`);
    safeSet(STORAGE_KEY, method);
    if (method === "MANUAL") {
      safeSet(SKIP_KEY, "1");
    } else {
      try { localStorage.removeItem(SKIP_KEY); } catch {}
    }
    setCurrentMethod(method);
  };

  const ctxValue = useMemo<AuthRouterContextType>(() => ({ setAuthMethod, currentMethod }), [currentMethod]);

  if (!authReady) {
    return (
        <div style={{ padding: 16 }} role="status" aria-live="polite">
          {t("authRouter.loading")} <small>({status})</small>
        </div>
    );
  }

  logger.debug("[AuthRouter] ready; currentMethod =", currentMethod);

  return (
      <AuthRouterContext.Provider value={ctxValue}>
        {currentMethod === "MANUAL" ? (
            <ManualAuthProvider>{children}</ManualAuthProvider>
        ) : (
            <OidcAuthProvider>{children}</OidcAuthProvider>
        )}
      </AuthRouterContext.Provider>
  );
};
